import type { DeltaDisplayMode } from '../report-configurations/reportConfigurationsTypes';

// ================================================================================================
// Comparison delta display rules
//
// One implementation of how a period-over-period difference is *shown*, shared by the comparison
// bar chart's Difference chip and the comparison grid's $ Change cell. Two copies of these rules
// would disagree the first time either was tuned, and they sit inches apart on screen.
//
// Two axes, deliberately independent:
//
//   direction  — did this *category* move up or down? Derived by normalising the signed delta by
//                the row's expected sign. Never from the raw sign: QCS sends expenses negative, so
//                an expense that grew has a *negative* signed delta and still points ↑, because the
//                arrow describes the category, not the arithmetic.
//   sentiment  — is that movement good news? Income up is favorable, expense up is unfavorable.
//
// QCS DELTA_AMOUNT and DELTA_PERCENTAGE are arithmetic differences. Preserve those values and use
// `comparisonDelta*` when their sign is the available direction source. Where the caller also has
// both period values, derive direction from those values and pass it separately; this keeps a
// server-owned display value from also becoming an implicit client navigation/sentiment rule.
//
// Which axis is rendered depends on `deltaDisplayMode`, independently from expense amount signs:
//
//   SIGN  → no arrows anywhere. Show the true signed delta with an explicit + or -.
//   ARROW → no sign anywhere. Show the category movement with an arrow.
//
// Colour comes from sentiment in *both* modes, so the two never collapse into one another.
// ================================================================================================

/** Mirrors `ReportRow['expectedSign']`. `undefined` is a row the server said nothing about. */
export type ComparisonDeltaExpectedSign = 'POSITIVE' | 'NEGATIVE' | 'ANY' | undefined;

/** Which way the category moved. `null` means it did not move, or there is nothing to compare. */
export type ComparisonDeltaDirection = 'up' | 'down' | null;

/** How the movement reads, independent of which way the arrow points. */
export type ComparisonDeltaSentimentValue = 'favorable' | 'unfavorable' | 'neutral';

/** Everything a delta renderer needs after its movement has been resolved from the period values. */
export type ComparisonDeltaPresentation = {
  direction: ComparisonDeltaDirection;
  arrow: ComparisonDeltaDirection;
  sentiment: ComparisonDeltaSentimentValue;
  sign: '' | '+' | '-';
};

/** Whether a known movement direction is favorable for this row. */
const comparisonDirectionSentiment = (
  direction: ComparisonDeltaDirection,
  expectedSign: ComparisonDeltaExpectedSign,
): ComparisonDeltaSentimentValue => {
  if (!direction) return 'neutral';
  if (expectedSign === 'NEGATIVE') return direction === 'up' ? 'unfavorable' : 'favorable';
  return direction === 'up' ? 'favorable' : 'unfavorable';
};

/**
 * Whether an amount rounds away to zero at the precision it will be shown in.
 *
 * Matters because a delta of half a cent must not be given an arrow: the amount beside it would
 * read `$0.00`, and an arrow next to a zero claims a movement the number denies.
 */
export const isDeltaDisplayedAsZero = (value: number, hideCents = false): boolean =>
  Math.round(Math.abs(value) * (hideCents ? 1 : 100)) === 0;

/**
 * The signed delta re-expressed as movement in the direction the row is *expected* to move.
 *
 * Positive means the category grew. This is the single flip the whole rule rests on.
 */
const comparisonDeltaMovement = (signedDelta: number, expectedSign: ComparisonDeltaExpectedSign): number =>
  expectedSign === 'NEGATIVE' ? -signedDelta : signedDelta;

export const comparisonDeltaDirection = (
  signedDelta: number | null | undefined,
  expectedSign: ComparisonDeltaExpectedSign,
  hideCents = false,
): ComparisonDeltaDirection => {
  if (signedDelta == null || isDeltaDisplayedAsZero(signedDelta, hideCents)) return null;
  return comparisonDeltaMovement(signedDelta, expectedSign) > 0 ? 'up' : 'down';
};

/**
 * The sign prefix for the amount text: explicit `+`/`-` in signed mode, nothing in arrow mode.
 *
 * `+` is emitted rather than left implicit, because once the arrow is gone it is the only thing
 * distinguishing the two directions.
 */
const comparisonDeltaSignPrefix = (
  signedDelta: number | null | undefined,
  deltaDisplayMode: DeltaDisplayMode | boolean,
  hideCents = false,
): '' | '+' | '-' => {
  const resolvedMode = typeof deltaDisplayMode === 'boolean' ? (deltaDisplayMode ? 'SIGN' : 'ARROW') : deltaDisplayMode;
  if (resolvedMode !== 'SIGN' || signedDelta == null) return '';
  if (isDeltaDisplayedAsZero(signedDelta, hideCents)) return '';
  return signedDelta > 0 ? '+' : '-';
};

/**
 * Resolves the display intent once so arrows, signs and colours cannot use different inputs.
 *
 * `direction` comes from the two period values; `displayValue` remains the authoritative QCS
 * delta shown to the user. Keeping those inputs explicit prevents the display column from silently
 * becoming the source of movement when a difference column is hidden or changes convention.
 */
export const resolveComparisonDeltaPresentation = (
  direction: ComparisonDeltaDirection,
  displayValue: number | null | undefined,
  expectedSign: ComparisonDeltaExpectedSign,
  /** A boolean preserves the previous public API: `true` means signed mode. */
  deltaDisplayMode: DeltaDisplayMode | boolean,
  hideCents = false,
): ComparisonDeltaPresentation => {
  const resolvedMode = typeof deltaDisplayMode === 'boolean' ? (deltaDisplayMode ? 'SIGN' : 'ARROW') : deltaDisplayMode;
  return {
    direction,
    arrow: resolvedMode === 'SIGN' ? null : direction,
    sentiment: comparisonDirectionSentiment(direction, expectedSign),
    sign: comparisonDeltaSignPrefix(displayValue, resolvedMode, hideCents),
  };
};
